import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import {
  AbsoluteBox,
  Box,
  Clock,
  FlexBox,
  LegacyThemeProvider,
  Logo,
  MuiBox,
  MuiContainer,
  MuiThemeProvider,
  Stepper,
  StyledEngineProvider,
  useMediaQuery,
  useTheme
} from '@kis-technologies/kis-ui';
import { CartSummaryButton } from 'components/CartSummary';
import ProtectedRoutes from './components/ProtectedRoutes';
import { useActions } from 'hooks/useActions';
import { selectors as cartSelectors } from 'services/cart';
import { selectors as configSelectors } from 'services/configuration';
import { actions as kiosksActions, selectors as kiosksSelectors } from 'services/kiosk';
import { selectors as mediaSelectors } from 'services/media';
import i18n from 'services/translation/i18n';
import localTheme from 'theme';
import { routes } from './routesArray';
import { useStyles } from './styled';

// returns the index number to what the step index is within the stepper depending on the path
const getTicketingStep = (path: string) => {
  if (path.includes('cart')) return 1;
  if (path.includes('purchase')) return 2;
  if (path.includes('ticketing/delivery')) return 3;
  if (path.includes('completion')) return 4;
  return 0;
};

const getWillCallStep = (path: string) => {
  if (path.includes('lookup')) return 0;
  if (path.includes('orders')) return 1;
  if (path.includes('delivery')) return 2;
  if (path.includes('completion')) return 3;
  return 0;
};

const MainRoutes = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const absoluteMaintenancePaths = ['/', '/signin', '/startup', '/systems', '/printing', '/ui-automation'];
  const isMaintenancePath = path.includes('maintenance') || absoluteMaintenancePaths.includes(path);
  const isTicketingPath = path.includes('ticketing');
  const isWillCallPath = path.includes('willcall');
  const isThemed = isTicketingPath || isWillCallPath;

  // Actions
  const { listenSocketServer } = useActions({
    listenSocketServer: kiosksActions.listenSocketServer
  });

  // Selector
  const configuration = useSelector(configSelectors.selectConfiguration);
  const cart = useSelector(cartSelectors.selectCart);
  const cartPurchased = useSelector(cartSelectors.selectPurchased);
  const kiosk = useSelector(kiosksSelectors.selectKiosk);
  const logoImage = useSelector(mediaSelectors.selectLogoImage) || [];
  const clientId = get(kiosk, 'venue.clientId');
  const previewUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/logo_images/`;

  React.useEffect(() => {
    if (kiosk) {
      listenSocketServer({ kioskId: kiosk.id, configurationId: kiosk.configurationId, clientId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kiosk]);

  const themes = useTheme();
  const matchPortrait = useMediaQuery(themes.breakpoints.down('lg'));
  const flowTemplate: string = get(configuration, 'configuration.flowTemplate', '');
  const hideStepper = get(configuration, 'configuration.hideStepper', false);
  const cartLabel: string = get(configuration, `configuration.labels.cart.${i18n.language}`, i18n.t('cart.cartLabel'));
  const cartSummaryEnabled = get(configuration, 'ticketing.cart.summary.enabled');
  const palette = get(configuration, 'configuration.globalSettings.palette', { primary: '', secondary: '' });
  const fonts = get(configuration, 'configuration.globalSettings.fonts', { primary: '' });

  // Cache the step index, fires the getTicketingStep whenever path changes
  const ticketingStep = React.useMemo(() => getTicketingStep(path), [path]);
  const willCallStep = React.useMemo(() => getWillCallStep(path), [path]);

  // Setting the theme, using KIS or Client styles depending on the path
  const kisTheme = localTheme();
  const clientTheme = React.useMemo(() => {
    return localTheme({ palette, fonts });
  }, [palette, fonts]);
  const selectedTheme = isMaintenancePath ? kisTheme : clientTheme;

  // Opens the UI Automation Page for Devs (UIAutomationView)
  const pageOptions = () => {
    if (`${process.env.NODE_ENV}` !== 'production') {
      navigate('/ui-automation');
    }
  };

  const localHostBaseUrl = `${process.env.REACT_APP_HARDWARE_API_BASE}`;
  const [offlineLimit, setOfflineLimit] = React.useState(0);
  const [status, setStatus] = React.useState(true);

  React.useEffect(() => {
    const changeStatus = () => {
      setStatus(navigator.onLine);
    };
    window.addEventListener('online', changeStatus);
    window.addEventListener('offline', changeStatus);
    return () => {
      window.removeEventListener('online', changeStatus);
      window.removeEventListener('offline', changeStatus);
    };
  }, []);

  React.useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (!window.navigator.onLine) {
      timeoutId = setTimeout(() => {
        setOfflineLimit(offlineLimit + 1);
      }, 2000);
    } else {
      setOfflineLimit(0);
    }

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, offlineLimit]);

  const stepperBlacklist = ['attract', 'start-purchase', 'offline', 'info', 'splash', 'donation'];
  // displays the stepper if it is a ticketing path but is neither (AttractLoopView) nor (StartPurchaseView)
  const displayStepper = !(stepperBlacklist.filter((route) => path.includes(route)).length > 0);
  const displayHeader =
    flowTemplate === 'fair'
      ? ['maintenance', 'systems', 'printing'].filter((route) => path.includes(route)).length > 0
      : true;

  // Black listing the path for cartSummary button
  const lastIndex = path.lastIndexOf('/');
  const pageName = path.substring(lastIndex + 1);
  const cartSummaryBlackList = [
    'attract',
    'start-purchase',
    'offline',
    'info',
    'splash',
    'cart',
    'customer',
    'survey',
    'delivery-selection',
    'payment'
  ];
  const displayCartSummaryButton = !(cartSummaryBlackList.filter((route) => pageName.includes(route)).length > 0);

  // Set background color for promo code approval page, to catch attention of an attendant
  const backgroundColor = path.includes('promo-code/approval') ? palette.primary : undefined;

  const classes = useStyles({ flowTemplate, displayHeader, backgroundColor })();

  if (offlineLimit > 5) {
    window.open(`${localHostBaseUrl}/network-check`, '_self');
  }

  return (
    <StyledEngineProvider injectFirst>
      <LegacyThemeProvider theme={selectedTheme}>
        <MuiThemeProvider theme={selectedTheme}>
          <MuiBox className={classes.routerOuterBox}>
            <MuiContainer className={classes.routerContainer} maxWidth="lg">
              <MuiBox className={classes.routeContainerBox}>
                {/* Header */}
                {displayHeader && (
                  <FlexBox justify="between" margin={isThemed && logoImage && logoImage.length > 0 ? '0 5' : '0 5 5'}>
                    {/* Logo */}
                    <FlexBox flexOverride="1">
                      <div
                        className="ui-automation"
                        onClick={pageOptions}
                        data-testid="ui-automation-page"
                        style={isThemed && logoImage && logoImage.length > 0 ? { height: 100 } : {}}
                      >
                        {isThemed && logoImage && logoImage.length > 0 ? (
                          <img alt="url" src={`${previewUrl}${logoImage[0].s3Name}`} style={{ height: 100 }} />
                        ) : (
                          <Logo />
                        )}
                      </div>
                    </FlexBox>
                    {/* Stepper */}
                    {!hideStepper && displayStepper && isTicketingPath && !matchPortrait && (
                      <Box flexOverride="1 0 auto">
                        <Stepper activeStep={ticketingStep}>
                          <Stepper.Step label={i18n.t('stepper.tickets')} />
                          <Stepper.Step
                            label={i18n.t('stepper.cart', {
                              cartLabel: cartLabel.charAt(0).toUpperCase() + cartLabel.slice(1)
                            })}
                          />
                          <Stepper.Step label={i18n.t('stepper.purchase')} />
                          <Stepper.Step label={i18n.t('stepper.delivery')} />
                          <Stepper.Step label={i18n.t('stepper.completion')} />
                        </Stepper>
                      </Box>
                    )}
                    {!hideStepper && displayStepper && isWillCallPath && !matchPortrait && (
                      <Box width={804} flexOverride="1">
                        <Stepper activeStep={willCallStep}>
                          <Stepper.Step label={i18n.t('stepper.lookupTickets')} />
                          <Stepper.Step label={i18n.t('stepper.confirmTickets')} />
                          <Stepper.Step label={i18n.t('stepper.delivery')} />
                          <Stepper.Step label={i18n.t('stepper.completion')} />
                        </Stepper>
                      </Box>
                    )}
                    {/* Cart summary button */}
                    <FlexBox flexOverride="1 0 auto" justify="end">
                      {displayCartSummaryButton && cartSummaryEnabled && cart && !cartPurchased && isTicketingPath && (
                        <Box sx={{ position: 'relative' }}>
                          <AbsoluteBox right="15px" top="5px" maxWidth={350}>
                            <CartSummaryButton />
                          </AbsoluteBox>
                        </Box>
                      )}
                      <Clock t={t} />
                    </FlexBox>
                  </FlexBox>
                )}

                <Routes>
                  {/* Routes that neither ticketing nor protected */}
                  {routes
                    .filter((route) => !route.ticketing && !route.protected)
                    .map((route) => (
                      <Route path={route.path} element={<route.component />} key={route.path} />
                    ))}
                  {/* ProtectedRoutes Component */}
                  <Route path="/*" element={<ProtectedRoutes />} />
                </Routes>
              </MuiBox>
            </MuiContainer>
          </MuiBox>
        </MuiThemeProvider>
      </LegacyThemeProvider>
    </StyledEngineProvider>
  );
};

export default MainRoutes;
